/**
 * Ingest Ops Tab
 *
 * Shows API Manager ingest history + a minimal replay control.
 * Backend source of truth:
 * - GET  /api/ingest/history
 * - GET  /api/ingest/status
 * - POST /api/ingest/replay/:ingest_id   (ops-only)
 */
import React, { useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Divider,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
  Paper,
} from '@mui/material';
import {
  Refresh as RefreshIcon,
  Replay as ReplayIcon,
} from '@mui/icons-material';

import apiManagerApi from './api';

type IngestHistoryItem = {
  ingest_id: string;
  tenant: string;
  app_id?: string | null;
  provider_key?: string;
  api_id?: string;
  endpoint_id?: string;
  mode?: string;
  status?: string;
  http_status?: number | null;
  created_at?: string;
  processing_time_ms?: number;
  replay_count?: number;
  last_replay_at?: string;
};

function statusColor(status?: string): any {
  switch (status) {
    case 'published':
    case 'stored':
    case 'sent':
    case 'success':
      return 'success';
    case 'failed':
      return 'error';
    default:
      return 'default';
  }
}

export default function IngestOpsTab() {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [busyReplayId, setBusyReplayId] = useState<string | null>(null);
  const [items, setItems] = useState<IngestHistoryItem[]>([]);
  const [counts, setCounts] = useState<{ provider_key?: string; status?: string; count: number }[]>([]);
  const [overview, setOverview] = useState<any[]>([]);

  const [windowMinutes, setWindowMinutes] = useState<number>(60);
  const [providerKey, setProviderKey] = useState<string>('');
  const [apiId, setApiId] = useState<string>('');
  const [status, setStatus] = useState<string>('');
  const [limit, setLimit] = useState<number>(50);

  const queryParams = useMemo(() => {
    const p: any = { limit };
    if (providerKey) p.provider_key = providerKey;
    if (apiId) p.api_id = apiId;
    if (status) p.status = status;
    return p;
  }, [providerKey, apiId, status, limit]);

  async function load() {
    try {
      setLoading(true);
      setError(null);
      const [h, s, o] = await Promise.all([
        apiManagerApi.ingestHistory(queryParams),
        apiManagerApi.ingestStatus({ window_minutes: windowMinutes }),
        apiManagerApi.ingestOverview({ window_minutes: windowMinutes, api_id: apiId || undefined }),
      ]);
      setItems(h.data.history || []);
      setCounts(s.data.counts || []);
      setOverview(o?.data?.overview || []);
    } catch (e: any) {
      setError(e?.response?.data?.error || e?.message || 'Failed to load ingest ops');
      setItems([]);
      setCounts([]);
      setOverview([]);
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [windowMinutes]);

  const handleReplay = async (ingestId: string) => {
    if (!confirm(`Replay ingest ${ingestId}?\n\nThis will re-publish the stored envelope back to Pulsar.`)) return;
    try {
      setBusyReplayId(ingestId);
      setError(null);
      await apiManagerApi.replayIngest(ingestId);
      await load();
    } catch (e: any) {
      setError(e?.response?.data?.error || e?.response?.data?.message || e?.message || 'Replay failed');
    } finally {
      setBusyReplayId(null);
    }
  };

  const exportJson = (payload: any, filename: string) => {
    const blob = new Blob([JSON.stringify(payload, null, 2)], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    a.remove();
    URL.revokeObjectURL(url);
  };

  return (
    <Box sx={{ px: 3 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
        <Box>
          <Typography variant="h6">Ingest Ops</Typography>
          <Typography variant="body2" color="text.secondary">
            Visibility and replay for events published to Pulsar ingest.raw.&lt;tenant&gt;
          </Typography>
        </Box>
        <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
          <Button
            variant="outlined"
            onClick={() => exportJson({ window_minutes: windowMinutes, overview, history: items, counts }, `api-ingest-ops-${Date.now()}.json`)}
            disabled={loading}
          >
            Export logs (JSON)
          </Button>
          <Button
            variant="outlined"
            startIcon={<RefreshIcon />}
            onClick={load}
            disabled={loading}
          >
            Refresh
          </Button>
        </Box>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      <Paper sx={{ p: 2, mb: 2 }}>
        <Typography variant="subtitle2" sx={{ mb: 1 }}>
          Overview (per API → endpoint, last {windowMinutes} minutes)
        </Typography>
        {overview.length === 0 && (
          <Typography variant="body2" color="text.secondary">
            No ingest activity in this window.
          </Typography>
        )}
        {overview.length > 0 && (
          <TableContainer component={Paper} variant="outlined" sx={{ mb: 2 }}>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>API</TableCell>
                  <TableCell>Endpoint</TableCell>
                  <TableCell>Last ingest</TableCell>
                  <TableCell align="right">Published</TableCell>
                  <TableCell align="right">Failed</TableCell>
                  <TableCell>Timescale last</TableCell>
                  <TableCell align="right">Timescale count</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {overview.flatMap((apiRow: any) =>
                  (apiRow.endpoints || []).map((ep: any) => {
                    const statuses = Array.isArray(ep.statuses) ? ep.statuses : [];
                    const published = statuses.find((s: any) => s.status === 'published')?.count || 0;
                    const failed = statuses.find((s: any) => s.status === 'failed')?.count || 0;
                    return (
                      <TableRow key={`${apiRow.api_id}:${ep.endpoint_id}`}>
                        <TableCell>{apiRow.api_id}</TableCell>
                        <TableCell>{ep.endpoint_id}</TableCell>
                        <TableCell>{ep.last_at ? new Date(ep.last_at).toLocaleString() : '-'}</TableCell>
                        <TableCell align="right">{published}</TableCell>
                        <TableCell align="right">{failed}</TableCell>
                        <TableCell>{ep.timescale_last_time ? new Date(ep.timescale_last_time).toLocaleString() : '-'}</TableCell>
                        <TableCell align="right">{ep.timescale_count_window ?? '-'}</TableCell>
                      </TableRow>
                    );
                  })
                )}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </Paper>

      <Paper sx={{ p: 2, mb: 2 }}>
        <Typography variant="subtitle2" sx={{ mb: 1 }}>
          Status (last {windowMinutes} minutes)
        </Typography>
        {counts.length === 0 && (
          <Typography variant="body2" color="text.secondary">
            No ingest activity in this window.
          </Typography>
        )}
        {counts.length > 0 && (
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
            {counts.map((c, idx) => (
              <Chip
                key={`${c.provider_key || 'unknown'}:${c.status || 'unknown'}:${idx}`}
                label={`${c.provider_key || 'unknown'} • ${c.status || 'unknown'}: ${c.count}`}
                size="small"
                color={statusColor(c.status)}
                variant="outlined"
              />
            ))}
          </Box>
        )}

        <Box sx={{ mt: 2, display: 'flex', gap: 2, alignItems: 'center', flexWrap: 'wrap' }}>
          <TextField
            label="Provider"
            size="small"
            value={providerKey}
            onChange={(e) => setProviderKey(e.target.value)}
            placeholder="e.g. agrirouter"
          />
          <TextField
            label="API ID"
            size="small"
            value={apiId}
            onChange={(e) => setApiId(e.target.value)}
            placeholder="e.g. agrirouter_ab12cd34"
          />
          <FormControl size="small" sx={{ minWidth: 160 }}>
            <InputLabel>Status</InputLabel>
            <Select value={status} label="Status" onChange={(e) => setStatus(String(e.target.value))}>
              <MenuItem value="">Any</MenuItem>
              <MenuItem value="published">published</MenuItem>
              <MenuItem value="stored">stored</MenuItem>
              <MenuItem value="failed">failed</MenuItem>
            </Select>
          </FormControl>
          <FormControl size="small" sx={{ minWidth: 140 }}>
            <InputLabel>Limit</InputLabel>
            <Select value={limit} label="Limit" onChange={(e) => setLimit(Number(e.target.value))}>
              <MenuItem value={25}>25</MenuItem>
              <MenuItem value={50}>50</MenuItem>
              <MenuItem value={100}>100</MenuItem>
              <MenuItem value={200}>200</MenuItem>
            </Select>
          </FormControl>

          <FormControl size="small" sx={{ minWidth: 200 }}>
            <InputLabel>Window</InputLabel>
            <Select value={windowMinutes} label="Window" onChange={(e) => setWindowMinutes(Number(e.target.value))}>
              <MenuItem value={15}>15 minutes</MenuItem>
              <MenuItem value={60}>60 minutes</MenuItem>
              <MenuItem value={240}>4 hours</MenuItem>
              <MenuItem value={1440}>24 hours</MenuItem>
            </Select>
          </FormControl>

          <Button variant="contained" onClick={load} disabled={loading}>
            Apply filters
          </Button>
        </Box>
      </Paper>

      <Divider sx={{ mb: 2 }} />

      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
          <CircularProgress />
        </Box>
      ) : (
        <TableContainer component={Paper}>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Created</TableCell>
                <TableCell>Status</TableCell>
                <TableCell>Provider</TableCell>
                <TableCell>API</TableCell>
                <TableCell>Endpoint</TableCell>
                <TableCell>Mode</TableCell>
                <TableCell align="right">HTTP</TableCell>
                <TableCell align="right">ms</TableCell>
                <TableCell>Ingest ID</TableCell>
                <TableCell align="right">Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {items.length === 0 && (
                <TableRow>
                  <TableCell colSpan={10}>
                    <Typography variant="body2" color="text.secondary">
                      No ingest history found for these filters.
                    </Typography>
                  </TableCell>
                </TableRow>
              )}
              {items.map((it) => (
                <TableRow key={it.ingest_id}>
                  <TableCell>{it.created_at ? new Date(it.created_at).toLocaleString() : '—'}</TableCell>
                  <TableCell>
                    <Chip size="small" label={it.status || 'unknown'} color={statusColor(it.status)} />
                  </TableCell>
                  <TableCell>{it.provider_key || '—'}</TableCell>
                  <TableCell>{it.api_id || '—'}</TableCell>
                  <TableCell>{it.endpoint_id || '—'}</TableCell>
                  <TableCell>{it.mode || '—'}</TableCell>
                  <TableCell align="right">{typeof it.http_status === 'number' ? it.http_status : '—'}</TableCell>
                  <TableCell align="right">{typeof it.processing_time_ms === 'number' ? it.processing_time_ms : '—'}</TableCell>
                  <TableCell>
                    <code style={{ fontSize: 12 }}>{it.ingest_id}</code>
                  </TableCell>
                  <TableCell align="right">
                    <Tooltip title="Replay: republishes stored envelope to Pulsar (ops-only)">
                      <span>
                        <Button
                          size="small"
                          startIcon={busyReplayId === it.ingest_id ? <CircularProgress size={16} /> : <ReplayIcon />}
                          disabled={!!busyReplayId}
                          onClick={() => handleReplay(it.ingest_id)}
                        >
                          Replay
                        </Button>
                      </span>
                    </Tooltip>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}
    </Box>
  );
}



